/**
 * Keyboard-wedge scanner capture with automatic scan-vs-typing detection.
 *
 * The listener detects the fast character bursts produced by USB scanners,
 * swallows the raw payload, and calls onScan once the transmission completes.
 */

export const GS = '\x1D';

export const SCANNER_TIMING = Object.freeze({
  burstGapMs: 35,
  burstMinChars: 4,
  finalizeSilenceMs: 120,
  terminatorGraceMs: 80,
  minScanLength: 8,
});

export function createScannerListener(onScan, options = {}) {
  const { onLeakCleanup } = options;
  let buffer = [];
  let scanMode = false;
  let silenceTimer = null;
  let pendingTerminator = false;

  const isEditable = (element) => (
    element
    && (element.tagName === 'INPUT' || element.tagName === 'TEXTAREA' || element.isContentEditable)
  );

  const reset = () => {
    buffer = [];
    scanMode = false;
    pendingTerminator = false;
    if (silenceTimer) {
      clearTimeout(silenceTimer);
      silenceTimer = null;
    }
  };

  const finalize = () => {
    const raw = buffer.map((entry) => entry.char).join('');
    const wasScan = scanMode && raw.length >= SCANNER_TIMING.minScanLength;
    reset();
    if (wasScan) onScan(raw);
  };

  const undoLeaks = () => {
    const touched = new Set();

    for (let index = buffer.length - 1; index >= 0; index--) {
      const key = buffer[index];
      if (!key.leaked || !isEditable(key.target)) continue;
      const element = key.target;
      if (element.value.endsWith(key.char)) {
        element.value = element.value.slice(0, -key.char.length);
      }
      key.leaked = false;
      touched.add(element);
    }

    if (onLeakCleanup) {
      onLeakCleanup(Array.from(touched).map((target) => ({ target, value: target.value })));
      return;
    }

    for (const element of touched) {
      element.dispatchEvent(new Event('input', { bubbles: true }));
    }
  };

  const onKeydown = (event) => {
    if (['Shift', 'Control', 'Alt', 'Meta'].includes(event.key)) return;

    const now = performance.now();
    const last = buffer[buffer.length - 1];
    const gap = last ? now - last.time : Infinity;

    let char = null;
    if (event.ctrlKey && (event.key === ']' || event.key === '\u001d')) char = GS;
    else if (!event.ctrlKey && !event.metaKey && !event.altKey && event.key.length === 1) char = event.key;

    const isTerminator = event.key === 'Enter' || event.key === 'Tab';

    if (scanMode) {
      event.preventDefault();
      event.stopPropagation();

      if (isTerminator) {
        pendingTerminator = true;
        if (silenceTimer) clearTimeout(silenceTimer);
        silenceTimer = setTimeout(finalize, SCANNER_TIMING.terminatorGraceMs);
        return;
      }

      if (char !== null) {
        if (pendingTerminator) {
          buffer.push({ char: GS, time: now, target: null, leaked: false });
          pendingTerminator = false;
        }
        buffer.push({ char, time: now, target: null, leaked: false });
        if (silenceTimer) clearTimeout(silenceTimer);
        silenceTimer = setTimeout(finalize, SCANNER_TIMING.finalizeSilenceMs);
      }
      return;
    }

    if (char === null) {
      if (!isTerminator) reset();
      return;
    }

    if (gap > SCANNER_TIMING.burstGapMs) buffer = [];

    buffer.push({
      char,
      time: now,
      target: event.target,
      leaked: isEditable(event.target),
    });

    if (buffer.length >= SCANNER_TIMING.burstMinChars) {
      scanMode = true;
      event.preventDefault();
      event.stopPropagation();
      buffer[buffer.length - 1].leaked = false;
      undoLeaks();
      silenceTimer = setTimeout(finalize, SCANNER_TIMING.finalizeSilenceMs);
    }
  };

  document.addEventListener('keydown', onKeydown, true);

  return {
    simulateScan(raw) {
      onScan(raw);
    },
    destroy() {
      document.removeEventListener('keydown', onKeydown, true);
      reset();
    },
  };
}
